import { useAuth0 } from '@auth0/auth0-react';
import { DOMAIN, LoggerFactory, FEATURE } from 'Services/Logger';
import { useQuery } from '@tanstack/react-query';

const logger = LoggerFactory.getLogger(DOMAIN.CONSUMER_ENABLEMENT);

/**
 * useSessionId hook to fetch session ID (sid) for an authenticated user.
 *
 * @requires Auth0 session - This hook depends on the user being authenticated through Auth0.
 *
 * @returns {string|null} The session ID (`sid`) from the user's Auth0 token claims.
 * Returns `null` if the user is not authenticated or if the `sid` is not available.
 *
 * @example
 * // Usage within a Component or Hook:
 * const sessionId = useSessionId();
 */
export const useSessionId = (): string | null => {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();

  const fetchSessionId = async (): Promise<string | null> => {
    if (isAuthenticated) {
      try {
        const claims = await getIdTokenClaims();
        const isTokenExpired = claims && claims.exp && claims.exp * 1000 < Date.now();

        if (isTokenExpired) {
          return null;
        }

        const sid = claims?.sid;
        return sid || null;
      } catch (error) {
        logger.error(
          'Failed to fetch session ID from Auth0 token claims',
          `${DOMAIN.CONSUMER_ENABLEMENT}.${FEATURE.AUTHENTICATION}`,
          {
            error,
          },
        );
        return null;
      }
    }
    return null;
  };

  const { data: sessionId } = useQuery<string | null>(['AUTH0_SESSION_ID'], fetchSessionId, {
    placeholderData: null,
  });

  return sessionId ?? null;
};
